// TwoFactorGraceBanner.tsx — 1.22.00 badge⇔2FA enforcement. Shown to
// migration-cohort members (accounts verified before this release) while
// their 14-day grace countdown is running (User.badgeGraceExpiresAt), same
// dismissible-per-session pattern as VerifyEmailBanner.
import { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useSession } from '@/hooks';
import { Icon } from '@/icons';

const Bar = styled.div`
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  margin: 0 0 14px; padding: 10px 14px; border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) =>
    `color-mix(in srgb, ${theme.color.accentAmber} 14%, ${theme.color.glassBg})`};
  border: 1px solid transparent;
`;
const Text = styled.span`
  flex: 1; min-width: 0; font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;
const EnableLink = styled(Link)`
  font-weight: 600; font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.accentCyan};
  background: ${({ theme }) => `color-mix(in srgb, ${theme.color.accentCyan} 14%, transparent)`};
  border: 1px solid transparent; border-radius: ${({ theme }) => theme.radius.md};
  padding: 5px 12px; text-decoration: none; white-space: nowrap;
`;
const Dismiss = styled.button`
  background: transparent; border: none; cursor: pointer; padding: 4px; line-height: 0;
  color: ${({ theme }) => theme.color.textMuted};
`;

function daysLeft(iso: string): number {
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(1, Math.ceil(ms / (24 * 60 * 60 * 1000)));
}

export function TwoFactorGraceBanner() {
  const { currentUser } = useSession();
  const [dismissed, setDismissed] = useState(false);

  const expiresAt = currentUser?.badgeGraceExpiresAt;
  if (!expiresAt || dismissed) return null;

  const days = daysLeft(expiresAt);

  return (
    <Bar role="status">
      <Icon name="shield" size={16} />
      <Text>
        Enable two-factor authentication within {days} day{days === 1 ? '' : 's'} to keep your
        verified badge.
      </Text>
      <EnableLink to="/settings/privacy">Enable now</EnableLink>
      <Dismiss type="button" aria-label="Dismiss" onClick={() => setDismissed(true)}>
        <Icon name="close" size={16} />
      </Dismiss>
    </Bar>
  );
}
